import api from './api';

export const authService = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

export const userService = {
  list: () => api.get('/users'),
  create: (data: any) => api.post('/users', data),
  update: (userId: number, data: any) => api.put(`/users/${userId}`, data),
  auditLogs: () => api.get('/users/audit-logs'),
};

export const patientService = {
  list: () => api.get('/patients'),
  getById: (id: number) => api.get(`/patients/${id}`),
  create: (data: any) => api.post('/patients', data),
  update: (id: number, data: any) => api.put(`/patients/${id}`, data),
  delete: (id: number) => api.delete(`/patients/${id}`),
  searchByCPF: (cpf: string) => api.get('/patients/search/cpf', { params: { cpf } }),
  getPurchaseHistory: (id: number) => api.get(`/patients/${id}/history`),
};

export const honorariesService = {
  listByProfessional: (professionalId: number, startDate?: string, endDate?: string) =>
    api.get(`/honoraries/professional/${professionalId}`, {
      params: { startDate, endDate },
    }),
  generateReport: (professionalId: number, month: number, year: number) =>
    api.get(`/honoraries/${professionalId}/report`, {
      params: { month, year },
    }),
  sendEmail: (data: any) => api.post('/honoraries/send-email', data),
  markAsPaid: (participantId: number) =>
    api.put(`/honoraries/participants/${participantId}/mark-paid`),
  getPendingSummary: (month?: number, year?: number) =>
    api.get('/honoraries/pending/summary', {
      params: { month, year },
    }),
  listPayments: (params: any) => api.get('/honoraries/payments', { params }),
  updatePaymentStatus: (data: {
    participantId: number;
    status: 'pendente' | 'pago';
    paymentDate?: string;
  }) => api.put('/honoraries/payments/status', data),
};

export const paymentService = {
  list: (params: { status?: string; search?: string }) => api.get('/payments', { params }),
  markInstallmentAsPaid: (installmentId: number, paidAt?: string) =>
    api.put(`/payments/installments/${installmentId}/pay`, { paidAt }),
};

export const honoraryReceiptService = {
  listProfessionals: () => api.get('/honorary-receipts/professionals'),
  preview: (params: { professionalId: number; startDate: string; endDate: string }) =>
    api.get('/honorary-receipts/preview', { params }),
  listPaidHonoraries: () => api.get('/honorary-receipts/honoraries'),
  listHistory: () => api.get('/honorary-receipts/history'),
  getConfig: () => api.get('/honorary-receipts/config'),
  generatePdf: (data: any) => api.post('/honorary-receipts/pdf', data, { responseType: 'blob' }),
  sendEmail: (data: any) => api.post('/honorary-receipts/send', data),
};

export const reportService = {
  getMonthlySales: (month: number, year: number) =>
    api.get('/reports/monthly-sales', { params: { month, year } }),
  getProcedureProfitability: (startDate?: string, endDate?: string) =>
    api.get('/reports/procedure-profitability', {
      params: { startDate, endDate },
    }),
  getProfessionalPerformance: (
    startDate?: string,
    endDate?: string,
    specialty?: string
  ) =>
    api.get('/reports/professional-performance', {
      params: { startDate, endDate, specialty },
    }),
  getMonthlyExpenses: (month: number, year: number) =>
    api.get('/reports/monthly-expenses', { params: { month, year } }),
  getDelinquency: () => api.get('/reports/delinquency'),
  getDashboard: () => api.get('/reports/dashboard'),
};

export const domainService = {
  summary: () => api.get('/domain/summary'),
  registrations: () => api.get('/domain/registrations'),
  listProfessionals: (search = '') => api.get('/domain/professionals', { params: { search } }),
  createProfessional: (data: any) => api.post('/domain/professionals', data),
  updateProfessional: (id: number, data: any) => api.put(`/domain/professionals/${id}`, data),
  deleteProfessional: (id: number) => api.delete(`/domain/professionals/${id}`),
  listHospitals: (search = '') => api.get('/domain/hospitals', { params: { search } }),
  createHospital: (data: any) => api.post('/domain/hospitals', data),
  updateHospital: (id: number, data: any) => api.put(`/domain/hospitals/${id}`, data),
  deleteHospital: (id: number) => api.delete(`/domain/hospitals/${id}`),
  listProcedures: (search = '') => api.get('/domain/procedures', { params: { search } }),
  createProcedure: (data: any) => api.post('/domain/procedures', data),
  updateProcedure: (id: number, data: any) => api.put(`/domain/procedures/${id}`, data),
  deleteProcedure: (id: number) => api.delete(`/domain/procedures/${id}`),
  listMaterials: (search = '') => api.get('/domain/materials', { params: { search } }),
  createMaterial: (data: any) => api.post('/domain/materials', data),
  updateMaterial: (id: number, data: any) => api.put(`/domain/materials/${id}`, data),
  deleteMaterial: (id: number) => api.delete(`/domain/materials/${id}`),
};

export const systemService = {
  status: () => api.get('/system/data'),
  seed: (modelKey: string) => api.post(`/system/data/${modelKey}/seed`),
  clear: (modelKey: string, password: string, confirmation: string) =>
    api.delete(`/system/data/${modelKey}`, { data: { password, confirmation } }),
  reset: (password: string, confirmation: string) =>
    api.post('/system/reset', { password, confirmation }),
};
